import type { CashShiftSnapshot } from "../gateway/identity-client";
import { posRuntimeDb } from "./pos-db";

const currentShiftKey = "shift:current";
const shiftProjectionVersion = 2;

export type LocalShiftState = {
  shift: CashShiftSnapshot;
  source: "CORE" | "LOCAL";
  verifiedAt: string | null;
  projectionVersion?: number;
};

function scopeKey(shift: CashShiftSnapshot) {
  return `org:${shift.organization_id}:branch:${shift.branch_id}:node:${shift.node_id}`;
}

function sanitizeFrontlineShift(shift: CashShiftSnapshot): CashShiftSnapshot {
  return {
    ...shift,
    opening_cash: null,
    expected_cash: null,
    counted_cash: null,
    cash_difference: null,
    payment_summary: null,
    void_total: null,
    refund_total: null,
    cash_in_total: null,
    cash_out_total: null,
  };
}

export async function readLocalShift(): Promise<LocalShiftState | null> {
  try {
    const record = await posRuntimeDb.runtimeMeta.get(currentShiftKey);
    if (!record || !record.value || typeof record.value !== "object") return null;

    const value = record.value as Partial<LocalShiftState>;
    if (value.projectionVersion !== shiftProjectionVersion) {
      try {
        await posRuntimeDb.runtimeMeta.delete(currentShiftKey);
      } catch {
        // A stale secondary projection must not block Terminal recovery.
      }
      return null;
    }

    const shift = value.shift as CashShiftSnapshot | undefined;
    if (!shift?.shift_id || !shift.node_id || !shift.branch_id) return null;

    return {
      shift: sanitizeFrontlineShift(shift),
      source: value.source === "CORE" ? "CORE" : "LOCAL",
      verifiedAt: typeof value.verifiedAt === "string" ? value.verifiedAt : null,
      projectionVersion: shiftProjectionVersion,
    };
  } catch {
    // Shift recovery is secondary to Terminal recovery. A stale/malformed
    // shift projection must never make a trusted terminal look unactivated.
    return null;
  }
}

export async function saveCoreShift(shift: CashShiftSnapshot) {
  const now = new Date().toISOString();
  const safeShift = sanitizeFrontlineShift(shift);
  await posRuntimeDb.runtimeMeta.put({
    key: currentShiftKey,
    scopeKey: scopeKey(safeShift),
    value: {
      shift: safeShift,
      source: "CORE",
      verifiedAt: now,
      projectionVersion: shiftProjectionVersion,
    } satisfies LocalShiftState,
    updatedAt: now,
  });
}

export async function markShiftLocalOnly(shift: CashShiftSnapshot, verifiedAt: string | null) {
  const now = new Date().toISOString();
  const safeShift = sanitizeFrontlineShift(shift);
  await posRuntimeDb.runtimeMeta.put({
    key: currentShiftKey,
    scopeKey: scopeKey(safeShift),
    value: {
      shift: safeShift,
      source: "LOCAL",
      verifiedAt,
      projectionVersion: shiftProjectionVersion,
    } satisfies LocalShiftState,
    updatedAt: now,
  });
}

export async function clearLocalShift() {
  await posRuntimeDb.runtimeMeta.delete(currentShiftKey);
}
